<template>
  <div class="chest-editor">
    <div class="property-row">
      <span class="property-label">Name:</span>
      <input
        v-model="draft.name"
        class="gm-input chest-input"
        type="text"
        placeholder="Chest"
        @change="commit"
      />
    </div>

    <!-- Whether a chest exists here at all -->
    <div class="chest-field">
      <label class="chest-field__label">
        Chest spawn chance
        <span class="chest-field__value">{{ draft.chest_chance }}%</span>
      </label>
      <input
        v-model.number="draft.chest_chance"
        class="chest-slider"
        type="range"
        min="0"
        max="100"
        step="5"
        @change="commit"
      />
    </div>

    <div class="chest-row">
      <div class="chest-field chest-field--half">
        <label class="chest-field__label">Min items</label>
        <input
          v-model.number="draft.min_items"
          class="gm-input chest-number"
          type="number"
          min="0"
          max="20"
          @change="onBoundsChanged"
        />
      </div>
      <div class="chest-field chest-field--half">
        <label class="chest-field__label">Max items</label>
        <input
          v-model.number="draft.max_items"
          class="gm-input chest-number"
          type="number"
          min="0"
          max="20"
          @change="onBoundsChanged"
        />
      </div>
    </div>

    <!-- Per-slot chances. Consecutive, so the label says so plainly: a GM
         reading "2nd item 50%" as "half of all chests have two items" is the
         single easiest mistake to make here. -->
    <div v-if="draft.max_items > 0" class="chest-field">
      <label class="chest-field__label">Item slot chances</label>
      <p class="chest-hint">Each slot is rolled only if the one before it filled.</p>
      <div v-for="(chance, index) in draft.slot_chances" :key="index" class="chest-slot">
        <span class="chest-slot__index">{{ ordinal(index + 1) }} item</span>
        <input
          :value="chance"
          class="chest-slider chest-slot__slider"
          type="range"
          min="0"
          max="100"
          step="5"
          @input="setSlotChance(index, $event.target.valueAsNumber)"
          @change="commit"
        />
        <span class="chest-slot__value">{{ chance }}%</span>
      </div>
    </div>

    <!-- The reason the sliders are readable at all -->
    <div v-if="draft.max_items > 0" class="chest-outcome">
      <label class="chest-field__label">Resulting contents</label>
      <div v-for="(share, count) in distribution" :key="count" class="chest-outcome__row">
        <span class="chest-outcome__count">{{ count }} item{{ count === 1 ? '' : 's' }}</span>
        <span class="chest-outcome__bar">
          <span class="chest-outcome__fill" :style="{ width: `${(share * 100).toFixed(1)}%` }"></span>
        </span>
        <span class="chest-outcome__pct">{{ (share * 100).toFixed(0) }}%</span>
      </div>
      <p class="chest-hint chest-hint--tight">
        Of all cells, {{ Math.round(draft.chest_chance) }}% will have a chest at all.
      </p>
    </div>

    <!-- Loot table -->
    <div class="chest-field">
      <label class="chest-field__label">
        Loot table
        <span v-if="draft.loot.length" class="chest-field__value">{{ draft.loot.length }}</span>
      </label>

      <p v-if="!draft.loot.length" class="chest-hint chest-warn">
        No items configured — this chest will spawn empty.
      </p>

      <div v-for="entry in draft.loot" :key="entry.item_id" class="loot-entry">
        <img v-if="entry.icon" :src="entry.icon" :alt="entry.name" class="loot-entry__icon" />
        <span v-else class="loot-entry__icon loot-entry__icon--none">?</span>
        <span class="loot-entry__name" :title="entry.name">{{ entry.name }}</span>
        <input
          :value="entry.weight"
          class="gm-input loot-entry__weight"
          type="number"
          min="1"
          max="1000"
          :title="`Relative weight — ${weightShare(entry)}% of picks`"
          @change="setWeight(entry, $event.target.valueAsNumber)"
        />
        <span class="loot-entry__share">{{ weightShare(entry) }}%</span>
        <button class="gm-btn gm-btn--danger loot-entry__remove" title="Remove" @click="removeLoot(entry)">
          ×
        </button>
      </div>

      <!-- Item search -->
      <div class="loot-search">
        <input
          v-model="searchQuery"
          class="gm-input loot-search__input"
          type="text"
          placeholder="Search items to add…"
          @input="onSearchInput"
        />
        <select v-model="searchType" class="gm-select loot-search__type" @change="runSearch">
          <option value="">All types</option>
          <option v-for="type in itemTypes" :key="type" :value="type">{{ type }}</option>
        </select>
      </div>

      <p v-if="searching" class="chest-hint">Searching…</p>
      <p v-else-if="searchError" class="chest-hint chest-warn">{{ searchError }}</p>

      <div v-if="searchResults.length" class="loot-results">
        <button
          v-for="item in searchResults"
          :key="item.id"
          class="loot-result"
          :disabled="isInTable(item)"
          :title="item.description || item.name"
          @click="addLoot(item)"
        >
          <img v-if="item.icon" :src="item.icon" :alt="item.name" class="loot-result__icon" />
          <span v-else class="loot-result__icon loot-result__icon--none">?</span>
          <span class="loot-result__name">{{ item.name }}</span>
          <span class="loot-result__type">{{ item.type }}</span>
          <span v-if="isInTable(item)" class="loot-result__added">✓</span>
        </button>
      </div>
      <p v-else-if="searchQuery && !searching && !searchError" class="chest-hint">No items found.</p>
    </div>
  </div>
</template>

<script>
import chestService from '@/services/chestService';
import { chestSlotDistribution, createChestSpawnerProperties, reconcileChestSlots } from '@/types/tabletop';

const ITEM_TYPES = [
  'WEAPON', 'ARMOR', 'ARTIFACT', 'AMULET', 'MATERIAL', 'QUEST', 'MISC', 'FOOD', 'RUNE'
];

const SEARCH_DEBOUNCE_MS = 250;

export default {
  name: 'ChestSpawnerEditor',
  props: {
    /** The chest spawner's `properties` object from the selected cell. */
    modelValue: {
      type: Object,
      required: true
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      // Edited on a local copy and pushed up on commit, so a half-typed number
      // never lands in map_data and marks the map dirty on every keystroke.
      draft: this.normalise(this.modelValue),
      searchQuery: '',
      searchType: '',
      searchResults: [],
      searching: false,
      searchError: null,
      searchTimer: null,
      itemTypes: ITEM_TYPES
    };
  },
  computed: {
    distribution() {
      return chestSlotDistribution(this.draft);
    },
    totalWeight() {
      return this.draft.loot.reduce((sum, entry) => sum + (entry.weight || 1), 0);
    }
  },
  watch: {
    modelValue: {
      deep: true,
      handler(next) {
        this.draft = this.normalise(next);
      }
    }
  },
  mounted() {
    this.runSearch();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    normalise(properties) {
      return reconcileChestSlots({ ...createChestSpawnerProperties(), ...(properties || {}) });
    },
    commit() {
      this.draft = reconcileChestSlots(this.draft);
      this.$emit('update:modelValue', { ...this.draft, loot: [...this.draft.loot] });
    },
    onBoundsChanged() {
      // Bounds move the slot list, so reconcile before publishing or the
      // sliders and the numbers disagree for a frame.
      this.draft = reconcileChestSlots(this.draft);
      this.commit();
    },
    setSlotChance(index, value) {
      const chances = [...this.draft.slot_chances];
      chances[index] = Math.max(0, Math.min(100, value));
      this.draft = { ...this.draft, slot_chances: chances };
    },
    ordinal(n) {
      const suffix = ['th', 'st', 'nd', 'rd'][(n % 100 - 20) % 10] || ['th', 'st', 'nd', 'rd'][n % 100] || 'th';
      return `${n}${suffix}`;
    },
    weightShare(entry) {
      if (!this.totalWeight) return 0;
      return Math.round(((entry.weight || 1) / this.totalWeight) * 100);
    },
    isInTable(item) {
      return this.draft.loot.some(entry => entry.item_id === item.id);
    },
    addLoot(item) {
      if (this.isInTable(item)) return;
      this.draft = {
        ...this.draft,
        loot: [
          ...this.draft.loot,
          { item_id: item.id, name: item.name, icon: item.icon || '', weight: 1 }
        ]
      };
      this.commit();
    },
    removeLoot(entry) {
      this.draft = {
        ...this.draft,
        loot: this.draft.loot.filter(candidate => candidate.item_id !== entry.item_id)
      };
      this.commit();
    },
    setWeight(entry, value) {
      const weight = Math.max(1, Math.min(1000, Math.round(value || 1)));
      this.draft = {
        ...this.draft,
        loot: this.draft.loot.map(candidate =>
          candidate.item_id === entry.item_id ? { ...candidate, weight } : candidate
        )
      };
      this.commit();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.runSearch, SEARCH_DEBOUNCE_MS);
    },
    async runSearch() {
      this.searching = true;
      this.searchError = null;
      try {
        this.searchResults = await chestService.searchItems(this.searchQuery, this.searchType);
      } catch (error) {
        this.searchError = 'Could not load items. Are you signed in as a GM?';
        this.searchResults = [];
        console.warn('[ChestSpawnerEditor] item search failed:', error);
      } finally {
        this.searching = false;
      }
    }
  }
};
</script>

<style scoped>
.chest-editor {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.chest-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.chest-field--half {
  flex: 1;
}

.chest-row {
  display: flex;
  gap: var(--gm-gap-sm);
}

.chest-field__label {
  font-size: 0.7rem;
  color: var(--gm-text-muted);
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chest-field__value {
  color: var(--gm-accent);
}

.chest-hint {
  font-size: 0.64rem;
  color: var(--gm-text-muted);
  margin: 0.1rem 0;
  line-height: 1.3;
}

.chest-hint--tight {
  margin-top: 0.3rem;
}

.chest-warn {
  color: var(--gm-warning, #d8a13a);
}

.chest-input,
.chest-number {
  width: 100%;
  padding: 0.25rem 0.4rem;
  font-size: 0.72rem;
}

.chest-slider {
  width: 100%;
  accent-color: var(--gm-accent);
}

.chest-slot {
  display: grid;
  grid-template-columns: 4.6rem 1fr 2.2rem;
  align-items: center;
  gap: 0.35rem;
}

.chest-slot__index {
  font-size: 0.64rem;
  color: var(--gm-text-muted);
}

.chest-slot__value {
  font-size: 0.64rem;
  color: var(--gm-text);
  text-align: right;
}

.chest-outcome {
  background: var(--gm-surface-1, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius);
  padding: 0.4rem;
}

.chest-outcome__row {
  display: grid;
  grid-template-columns: 3.8rem 1fr 2.2rem;
  align-items: center;
  gap: 0.35rem;
  padding: 0.1rem 0;
}

.chest-outcome__count,
.chest-outcome__pct {
  font-size: 0.64rem;
  color: var(--gm-text-muted);
}

.chest-outcome__pct {
  text-align: right;
  color: var(--gm-text);
}

.chest-outcome__bar {
  height: 0.45rem;
  background: var(--gm-border-dim);
  border-radius: 0.25rem;
  overflow: hidden;
}

.chest-outcome__fill {
  display: block;
  height: 100%;
  background: var(--gm-accent);
  border-radius: 0.25rem;
}

.loot-entry {
  display: grid;
  grid-template-columns: 1.4rem 1fr 3rem 2.1rem 1.4rem;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0;
  border-bottom: 1px solid var(--gm-border-dim);
}

.loot-entry__icon,
.loot-result__icon {
  width: 1.4rem;
  height: 1.4rem;
  object-fit: contain;
  border-radius: 0.2rem;
  background: var(--gm-surface-2);
}

.loot-entry__icon--none,
.loot-result__icon--none {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: var(--gm-text-muted);
}

.loot-entry__name {
  font-size: 0.68rem;
  color: var(--gm-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loot-entry__weight {
  padding: 0.1rem 0.2rem;
  font-size: 0.66rem;
  width: 100%;
}

.loot-entry__share {
  font-size: 0.62rem;
  color: var(--gm-text-muted);
  text-align: right;
}

.loot-entry__remove {
  padding: 0 0.3rem;
  line-height: 1.2;
  font-size: 0.8rem;
}

.loot-search {
  display: flex;
  gap: 0.3rem;
  margin-top: 0.4rem;
}

.loot-search__input {
  flex: 1;
  padding: 0.25rem 0.4rem;
  font-size: 0.72rem;
}

.loot-search__type {
  padding: 0.25rem 0.3rem;
  font-size: 0.66rem;
}

.loot-results {
  max-height: 11rem;
  overflow-y: auto;
  margin-top: 0.3rem;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius);
}

.loot-result {
  display: grid;
  grid-template-columns: 1.4rem 1fr auto auto;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
  padding: 0.25rem 0.35rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--gm-border-dim);
  color: var(--gm-text);
  cursor: pointer;
  text-align: left;
}

.loot-result:hover:not(:disabled) {
  background: var(--gm-surface-2);
}

.loot-result:disabled {
  opacity: 0.45;
  cursor: default;
}

.loot-result__name {
  font-size: 0.68rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loot-result__type {
  font-size: 0.58rem;
  color: var(--gm-text-muted);
  text-transform: uppercase;
}

.loot-result__added {
  color: var(--gm-success);
  font-size: 0.7rem;
}
</style>
